import axios from 'axios';

import { useLocalStorage } from '@hooksApp/index';

// Variables to avoid multiple refresh requests
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

// Function to configure the base URL
const getBaseURL = (userData, domainRequest = true) => {
  const mode = {
    development: import.meta.env.VITE_BACKEND_DEV_BASE_URL,
    test: import.meta.env.VITE_BACKEND_TEST_BASE_URL,
    production: import.meta.env.VITE_BACKEND_PROD_BASE_URL
  };
  const baseURL = mode[import.meta.env.MODE];
  const finalHttp =
    import.meta.env.MODE === 'development' ? 'http://' : 'https://';
  const finalPort = import.meta.env.MODE === 'development' ? ':8000' : '';

  if (!domainRequest) return `${finalHttp}${baseURL}`;
  else return `${finalHttp}${userData.domain_url}${finalPort}`;
};

// Configure axios interceptors
const setupAxiosInterceptors = () => {
  // Response interceptor
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // Check if the error is an invalid token (401 or token_not_valid)
      if (
        (error.response?.status === 401 ||
          error.response?.data?.code === 'token_not_valid') &&
        !originalRequest._retry
      ) {
        if (isRefreshing) {
          // If already refreshing, add to the queue
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return axios(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const { userData, userRefresh, updateUserData } = useLocalStorage();

          // Configure the base URL for the refresh token
          const finalBaseURL = getBaseURL(userData, true); // Always use domainRequest for refresh token

          // Make refresh token request
          const refreshResponse = await axios.post(
            '/api/core/refresh-token/',
            { token: userRefresh },
            { baseURL: finalBaseURL }
          );

          const { access } = refreshResponse.data;

          // Update the token in localStorage
          updateUserData({ access });

          // Update the header of the original request
          originalRequest.headers.Authorization = `Bearer ${access}`;

          // Process the failed requests queue
          processQueue(null, access);

          // Retry the original request
          return axios(originalRequest);
        } catch (refreshError) {
          // If the refresh fails, clear localStorage and redirect to login
          processQueue(refreshError, null);
          localStorage.clear();
          window.location.href = '/';
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );

  // Request interceptor to add default headers
  axios.interceptors.request.use(
    (config) => {
      const { userAccess } = useLocalStorage();

      // Don't add authorization token for refresh-token requests
      if (config.url && config.url.includes('/api/core/refresh-token/')) {
        // Explicitly remove Authorization header for refresh token requests
        delete config.headers.Authorization;
        return config;
      }

      // Add authorization token if it exists for other requests
      if (userAccess && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${userAccess}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
};

// Initialize interceptors only once
let interceptorsInitialized = false;

const initializeAxiosInterceptors = () => {
  if (!interceptorsInitialized) {
    setupAxiosInterceptors();
    interceptorsInitialized = true;
  }
};

export { getBaseURL, initializeAxiosInterceptors };
